import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import Signal from "badmfck-signal";
import { Building2, Cable, Pencil, Plus, RotateCw } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import styled from "styled-components";
import { GD } from "src/GD";
import { useAPI } from "src/hooks/useApi";
import { notify } from "src/tools/notify";
import { S_CHANNEL_ITEM_POPUP, S_CHANNELS_LIST_REQUEST_UPDATE, type TPaymentChannel } from "../channelList/channelData";
import { S_PAYMENT_CHANNEL_CREATE } from "../channelList/Channels";
import { FormParser } from "../FormParser";
import { AdminButton } from "../pages/administration/AdminControls";
import { channelStatus, compareText, formatDate, groupBy, matchesQuery, methodTypeLabel, normalizeText, plural, summarizeChannels, type TChannelsSummary } from "./providersData";
import {
	baseFont,
	Card,
	CardBrandIcons,
	Cell,
	Chip,
	Chips,
	DialogBody,
	DirectoryCard,
	DirectoryRow,
	embeddedFormSx,
	EmptyState,
	EntityCell,
	FilterChips,
	IconButton,
	IconTile,
	Item,
	ItemList,
	LinkButton,
	MethodTile,
	Mono,
	Muted,
	Notice,
	ProviderDialog,
	RowChevron,
	SearchField,
	Section,
	SkeletonRows,
	StateDot,
	StatusPill,
	TabIntro,
	TabSurface,
	WarningText,
} from "./providersUi";

export type TPaymentProvider = {
	id: number;
	name: string;
	status: "active" | "inactive" | "deleted";
	created_at: number;
};

export const S_PAYMENT_PROVIDER_POPUP = new Signal<{
	model: { id: number };
	updater: (saved?: Partial<TPaymentProvider>) => void;
} | null>("payment_provider_popup");

export const S_PROVIDER_DETAILS_POPUP = new Signal<{ provider: TPaymentProvider; onChanged?: () => void } | null>("S_PROVIDER_DETAILS_POPUP");

type TStatusFilter = "all" | "active" | "inactive" | "deleted";

const MethodStack = ({ methods }: { methods: TChannelsSummary["methods"] }) =>
	{ useLocale(); return (methods.length ? (
		<Chips>
			{methods.map(method => (
				<MethodTile key={method.id} type={method.type} title={method.name} />
			))}
		</Chips>
	) : (
		<Muted>—</Muted>
	)); };

const CurrencyChips = ({ currencies }: { currencies: string[] }) =>
	{ useLocale(); return (currencies.length ? (
		<Chips>
			{currencies.map(currency => (
				<Chip key={currency}>{currency}</Chip>
			))}
		</Chips>
	) : (
		<Muted>—</Muted>
	)); };

/* ---------- Providers tab ---------- */

export const ProvidersList = ({
	providers,
	channels,
	loading,
	busy,
	error,
	onReload,
}: {
	providers: TPaymentProvider[];
	channels: TPaymentChannel[];
	loading: boolean;
	busy: boolean;
	error?: IError;
	onReload: () => void;
}) => {
	 useLocale();
	const [query, setQuery] = useState("");
	const [status, setStatus] = useState<TStatusFilter>("all");
	const summaries = useMemo(() => {
		const groups = groupBy(channels, channel => channel.provider?.id);
		return new Map(providers.map(provider => [provider.id, summarizeChannels(groups.get(provider.id) ?? [])]));
	}, [providers, channels]);
	const count = (value: TStatusFilter) => providers.filter(provider => value === "all" || provider.status === value).length;
	const visible = providers
		.filter(provider => status === "all" || provider.status === status)
		.filter(provider => matchesQuery(query, [provider.name, provider.id]))
		.sort((a, b) => compareText(a.name, b.name));
	const filtered = !!query || status !== "all";

	const edit = (id: number) =>
		S_PAYMENT_PROVIDER_POPUP.invoke({
			model: { id },
			updater: () => onReload(),
		});
	const open = (provider: TPaymentProvider) => S_PROVIDER_DETAILS_POPUP.invoke({ provider, onChanged: onReload });
	const addButton = (
		<AdminButton variant="primarySmall" size="small" onClick={() => edit(0)}>
			<Plus size={14} />{msg("addProvider")}</AdminButton>
	);

	return (
		<TabSurface>
			<TabIntro
				text={msg("providersAreTheCompaniesThatProcessYourPaymentsOpenAProvider_22edfb09")}
				actions={
					<>
						<AdminButton variant="secondarySmall" size="small" disabled={busy} onClick={onReload}>
							<RotateCw size={14} />{msg("refresh")}</AdminButton>
						{addButton}
					</>
				}
			/>
			{error && <Notice tone="error" action={<AdminButton variant="secondarySmall" size="small" onClick={onReload}>{msg("retry")}</AdminButton>}>{error.message}</Notice>}
			<DirectoryCard
				columns="minmax(240px, 1fr) minmax(100px, max-content) minmax(80px, max-content) minmax(120px, max-content) minmax(110px, max-content)"
				head={["Provider", "Methods", "Currencies", "Channels", "Status"]}
				toolbar={
					<>
						<SearchField value={query} onChange={setQuery} placeholder={msg("searchByNameOrId")} />
						<FilterChips
							label={msg("status")}
							value={status}
							onChange={setStatus}
							options={[
								{ id: "all", label: msg("all"), count: providers.length },
								{ id: "active", label: msg("active"), count: count("active") },
								{ id: "inactive", label: msg("inactive"), count: count("inactive"), hidden: !count("inactive") },
								{ id: "deleted", label: msg("deleted"), count: count("deleted"), hidden: !count("deleted") },
							]}
						/>
					</>
				}
				footer={
					!loading &&
					providers.length > 0 && (
						<>
							<span>{filtered ? msg("templateOf_64f93023", { v0: visible.length, v1: plural(providers.length, "provider") }) : plural(providers.length, "provider")}</span>
							{filtered && (
								<LinkButton
									onClick={() => {
										setQuery("");
										setStatus("all");
									}}>{msg("clearFilters")}</LinkButton>
							)}
						</>
					)
				}>
				{loading ? (
					<SkeletonRows />
				) : !providers.length ? (
					<EmptyState icon={<Building2 size={20} />} title={msg("noProvidersYet")} text={msg("addTheFirstProviderThenCreateItsChannels")} action={addButton} />
				) : !visible.length ? (
					<EmptyState icon={<Building2 size={20} />} title={msg("nothingMatchesTheFilters")} text={msg("tryAnotherSearchOrClearTheFilters")} />
				) : (
					visible.map(provider => {
						const summary = summaries.get(provider.id)!;
						const created = formatDate(provider.created_at);
						return (
							<DirectoryRow key={provider.id} label={msg("templateOpen_afaef5c3", { v0: provider.name })} muted={provider.status !== "active"} onOpen={() => open(provider)}>
								<Cell kind="main">
									<EntityCell
										icon={
											<IconTile>
												<Building2 />
											</IconTile>
										}
										name={provider.name || msg("templateProvider_c6409eb7", { v0: provider.id })}
										sub={
											<>
												<Mono>#{provider.id}</Mono>
												{created && <span>{msg("created_f21b8059")}<DateTimeTooltip value={provider.created_at}>{created}</DateTimeTooltip></span>}
											</>
										}
									/>
								</Cell>
								<Cell label={msg("methods")}>
									<MethodStack methods={summary.methods} />
								</Cell>
								<Cell label={msg("currencies")}>
									<CurrencyChips currencies={summary.currencies} />
								</Cell>
								<Cell label={msg("channels")}>
									{summary.total ? (
										<div>
											<LinkButton onClick={() => open(provider)}>{plural(summary.total, "channel")}</LinkButton>
											{summary.withoutIntegration > 0 && (
												<div>
													<WarningText>{summary.withoutIntegration}{msg("withoutIntegration")}</WarningText>
												</div>
											)}
										</div>
									) : (
										<LinkButton onClick={() => S_PAYMENT_CHANNEL_CREATE.invoke({ id: 0, payment_provider_id: provider.id, provider_name: provider.name })}>{msg("addFirstChannel")}</LinkButton>
									)}
								</Cell>
								<Cell kind="trail">
									<StatusPill status={provider.status} />
									<RowChevron />
								</Cell>
							</DirectoryRow>
						);
					})
				)}
			</DirectoryCard>
		</TabSurface>
	);
};

/* ---------- Provider dialog ---------- */

const Stats = styled.div`
	${baseFont};
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 12px;
	margin-bottom: 8px;
	.stat {
		min-width: 0;
		padding: 14px 16px;
		border: 1px solid #d0d5dd;
		border-radius: 10px;
		background: #fff;
		box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
	}
	.stat-label {
		color: #667085;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.04em;
		text-transform: uppercase;
	}
	.stat-value {
		margin-top: 4px;
		color: #182230;
		font-size: 20px;
		font-weight: 600;
		line-height: 1.2;
		font-variant-numeric: tabular-nums;
	}
	.stat-sub {
		margin-top: 2px;
		color: #667085;
		font-size: 12px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.stat.attention {
		border-color: #fedf89;
		background: #fffcf5;
	}
	.stat.attention .stat-value {
		color: #b54708;
	}
	@media (max-width: 720px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
`;

const ChannelLine = styled.div`
	.line-name {
		color: #182230;
		font-weight: 600;
		overflow-wrap: anywhere;
	}
	.line-sub {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 2px 8px;
		margin-top: 1px;
		color: #667085;
		font-size: 12px;
	}
`;

export const ProviderDetailsPopup = () => {
	useLocale();
	const request = S_PROVIDER_DETAILS_POPUP.use();
	if (!request) return null;
	return <ProviderDetails key={request.provider.id} provider={request.provider} onChanged={request.onChanged} />;
};

const ProviderDetails = ({ provider: initial, onChanged }: { provider: TPaymentProvider; onChanged?: () => void }) => {
	useLocale();
	const [provider, setProvider] = useState(initial);
	const [query, setQuery] = useState("");
	const [response, reload] = useAPI<TPaymentChannel[]>({ method: "payments/channels/list", data: { provider_id: initial.id } });
	S_CHANNELS_LIST_REQUEST_UPDATE.use([], () => void reload());
	const channels = Array.isArray(response?.data) ? response.data : [];
	const summary = summarizeChannels(channels);
	const deleted = channels.filter(channel => channelStatus(channel) === "deleted").length;
	const visible = channels.filter(channel =>
		matchesQuery(query, [channel.name, channel.id, channel.currency, channel.terminal?.name, channel.integration?.name, channel.integration?.type]),
	);
	const groups = [...groupBy(visible, channel => channel.terminal?.id ?? 0).entries()]
		.map(([id, items]) => ({ id, method: items[0]?.terminal, items: [...items].sort((a, b) => compareText(a.name, b.name)) }))
		.sort((a, b) => compareText(a.method?.name, b.method?.name));
	const created = formatDate(provider.created_at);
	const close = () => S_PROVIDER_DETAILS_POPUP.invoke(null);
	const addChannel = () => S_PAYMENT_CHANNEL_CREATE.invoke({ id: 0, payment_provider_id: provider.id, provider_name: provider.name, onSuccess: () => void reload() });
	const edit = () =>
		S_PAYMENT_PROVIDER_POPUP.invoke({
			model: { id: provider.id },
			updater: saved => {
				if (saved) setProvider(current => ({ ...current, ...saved, created_at: current.created_at }));
				onChanged?.();
			},
		});
	const addButton = (
		<AdminButton variant="primarySmall" size="small" onClick={addChannel}>
			<Plus size={14} />{msg("addChannel_62f66a6a")}</AdminButton>
	);

	return (
		<ProviderDialog
			width={900}
			fullHeight
			eyebrow={msg("paymentProvider")}
			title={provider.name || msg("templateProvider_c6409eb7", { v0: provider.id })}
			titleAction={
				<IconButton label={msg("editProvider")} tone="accent" onClick={edit}>
					<Pencil size={15} />
				</IconButton>
			}
			meta={
				<>
					<StateDot status={provider.status} />
					<Mono>#{provider.id}</Mono>
					{created && <span className="push">{msg("created_9c537ccd")}<DateTimeTooltip value={provider.created_at}>{created}</DateTimeTooltip></span>}
				</>
			}
			onClose={close}>
			<DialogBody>
				{provider.status !== "active" && (
					<Notice tone="warning">{msg("thisProviderIs")}{provider.status}{msg("itsChannelsStayConfiguredButCheckThatPaymentsAreNotRoutedToItByMistake")}</Notice>
				)}
				<Stats>
					<div className="stat">
						<div className="stat-label">{msg("channels")}</div>
						<div className="stat-value">{response ? summary.total : "—"}</div>
						<div className="stat-sub">{response ? msg("templateActive_c4e307a2", { v0: summary.active, v1: deleted ? msg("templateDeleted_3ca49076", { v0: deleted }) : "" }) : msg("loading_ba3bbbe1")}</div>
					</div>
					<div className="stat">
						<div className="stat-label">{msg("methods")}</div>
						<div className="stat-value">{response ? summary.methods.length : "—"}</div>
						<div className="stat-sub" title={summary.methods.map(method => method.name).join(", ")}>
							{summary.methods.map(method => method.name).join(", ") || "—"}
						</div>
					</div>
					<div className="stat">
						<div className="stat-label">{msg("currencies")}</div>
						<div className="stat-value">{response ? summary.currencies.length : "—"}</div>
						<div className="stat-sub">{summary.currencies.join(" · ") || "—"}</div>
					</div>
					<div className={`stat${summary.withoutIntegration ? " attention" : ""}`}>
						<div className="stat-label">{msg("needIntegration")}</div>
						<div className="stat-value">{response ? summary.withoutIntegration : "—"}</div>
						<div className="stat-sub">{summary.withoutIntegration ? msg("cannotProcessPaymentsYet") : msg("allChannelsAreConnected")}</div>
					</div>
				</Stats>

				<Section title={msg("channels")} count={response ? channels.length : undefined} action={channels.length > 0 && addButton} />
				{response?.error && <Notice tone="error">{response.error.message}</Notice>}
				{channels.length > 6 && (
					<div style={{ display: "flex", marginBottom: 12 }}>
						<SearchField value={query} onChange={setQuery} placeholder={msg("searchChannels")} />
					</div>
				)}
				{!response ? (
					<Card>
						<SkeletonRows rows={3} />
					</Card>
				) : !channels.length ? (
					<Card>
						<EmptyState
							icon={<Cable size={20} />}
							title={msg("noChannelsYet")}
							text={msg("aChannelIsAProviderPaymentMethodCurrencyCreateOneThenConnect_d5f3304b")}
							action={addButton}
						/>
					</Card>
				) : !visible.length ? (
					<Card>
						<EmptyState icon={<Cable size={20} />} title={msg("nothingMatchesTheSearch")} />
					</Card>
				) : (
					groups.map(group => {
						const type = methodTypeLabel(group.method?.type);
						return (
							<Fragment key={group.id}>
								<Section
									icon={<MethodTile type={group.method?.type} small />}
									title={group.method?.name ?? msg("noMethod")}
									count={group.items.length}
									action={normalizeText(type) !== normalizeText(group.method?.name) && <span style={{ color: "#98a2b3" }}>{type}</span>}
								/>
								<Card>
									<ItemList>
										{group.items.map(channel => (
											<ProviderChannelItem key={channel.id} channel={channel} />
										))}
									</ItemList>
								</Card>
							</Fragment>
						);
					})
				)}
			</DialogBody>
		</ProviderDialog>
	);
};

const ProviderChannelItem = ({ channel }: { channel: TPaymentChannel }) => { useLocale(); return ((
	<Item label={msg("templateOpen_afaef5c3", { v0: channel.name })} onOpen={() => S_CHANNEL_ITEM_POPUP.invoke(channel)}>
		<ChannelLine className="item-main">
			<div className="line-name">{channel.name || msg("templateChannel_e25c3617", { v0: channel.id })}</div>
			<div className="line-sub">
				<Mono>#{channel.id}</Mono>
				{channel.integration ? (
					<span>{msg("integration_90680a98")}<span style={{ color: "#344054" }}>{channel.integration.name}</span>
					</span>
				) : (
					<WarningText>{msg("noIntegration")}</WarningText>
				)}
			</div>
		</ChannelLine>
		<span className="item-side">
			<CardBrandIcons instrument={channel.instrument_type} />
			{channel.currency && <Chip>{channel.currency}</Chip>}
			<StatusPill status={channelStatus(channel)} />
		</span>
		<RowChevron />
	</Item>
)); };

/* ---------- Provider editor ---------- */

export const PaymentProviderEditor = () => {
	useLocale();
	const request = S_PAYMENT_PROVIDER_POPUP.use();
	if (!request) return null;
	const existing = request.model.id > 0;
	const close = () => S_PAYMENT_PROVIDER_POPUP.invoke(null);

	return (
		<ProviderDialog width={560} eyebrow={msg("paymentProvider")} title={existing ? msg("editProvider") : msg("newProvider")} onClose={close}>
			<DialogBody>
				<FormParser
					appearance="modern"
					sx={embeddedFormSx}
					size="small"
					secondaryButton={null}
					onSubmit={async (data: unknown): Promise<any> => {
						const response = await GD.REQ_API.request({ method: "payments/provider/set", data });
						if (response.error) return response;
						notify.success(existing ? msg("providerSaved") : msg("providerCreated"));
						request.updater(response.data && typeof response.data === "object" ? (response.data as Partial<TPaymentProvider>) : undefined);
						// Channels show their provider's name, so they are read again as well.
						S_CHANNELS_LIST_REQUEST_UPDATE.invoke();
						close();
						return data;
					}}
					onRequest={async (): Promise<any> => GD.REQ_API.request({ method: "payments/provider/get", data: { id: request.model.id } })}
				/>
			</DialogBody>
		</ProviderDialog>
	);
};
